import { ReactNode, useRef } from "react"
import { ComboboxInput } from "@ui/forms/inputs/combobox-input"
import { cn } from "@ui/utils"
import { Loader2 } from "lucide-react"

import { BaseInlineInput } from "./base"
import { InlineComboboxProps } from "./props"
import { useInlineEdit } from "./use-inline-edit"

export interface ComboboxOption<T = string> {
  label: string
  value: T
  icon?: ReactNode
}

export function InlineCombobox<T = string>({
  label,
  LabelIcon,
  hideLabel = false,
  size,
  placeholder = "Select an option",
  value: controlledValue,
  defaultValue,
  options,
  onChange,
  onSave,
  onSearch,
  isSaving = false,
  isSearching = false,
  disabled = false,
  hideLoader = false,
  emptyMessage = "No results found.",
  renderOption,
  fixedLabelWidth,
  emptyChildren,
}: InlineComboboxProps<T>) {
  const triggerRef = useRef<HTMLDivElement>(null)

  const { value, setValue, isSubmitting } = useInlineEdit({
    value: controlledValue,
    defaultValue,
    onChange,
    onSubmit: onSave,
    ref: triggerRef,
    disableClickOutside: true,
    submitOnChange: true,
    isEmpty: (val) => val === null || val === undefined,
  })

  function handleSelect(newValue: string) {
    // Convert string back to T type - this assumes T can be cast from string
    const typedValue = newValue as T
    setValue(typedValue)
  }

  // Convert our options to the format ComboboxInput expects
  const comboboxOptions = options.map((option) => ({
    label: typeof option.label === "string" ? option.label : String(option.value),
    value: String(option.value),
    icon: option.icon,
  }))

  return (
    <BaseInlineInput
      label={label}
      LabelIcon={LabelIcon}
      hideLabel={hideLabel}
      size={size}
      fixedLabelWidth={fixedLabelWidth}
    >
      <div className="relative w-full">
        <div ref={triggerRef}>
          <ComboboxInput
            id="inline-combobox"
            value={value ? String(value) : ""}
            onChange={handleSelect}
            options={comboboxOptions}
            onSearch={onSearch}
            isSearching={isSearching}
            placeholder={placeholder}
            emptyText={emptyMessage}
            disabled={disabled}
            renderOption={
              renderOption
                ? (option) => {
                    const originalOption = options.find((opt) => String(opt.value) === option.value)
                    return originalOption ? renderOption(originalOption) : null
                  }
                : undefined
            }
            triggerButton={
              <button
                type="button"
                className={cn(
                  "focus:outline-primary flex h-8 w-full items-center justify-between rounded-md border-1 border-transparent px-2 shadow-none focus:outline-1 md:text-sm",
                  !value && "text-input-placeholder",
                  disabled ? "cursor-default opacity-50" : "hover:bg-muted cursor-pointer"
                )}
                disabled={disabled}
              >
                <span className={cn("truncate", !value && "text-input-placeholder")}>
                  {value ? options.find((opt) => opt.value === value)?.label : placeholder}
                </span>
              </button>
            }
            contentProps={{ align: "start" }}
            emptyChildren={emptyChildren}
          />
        </div>
        {!hideLoader && (isSaving || isSubmitting) && (
          <Loader2 className="text-icon absolute top-2 right-2 h-4 w-4 animate-spin" />
        )}
      </div>
    </BaseInlineInput>
  )
}
